import {
  ChangeMeta,
  PagedResponse,
  PatchWithMeta,
  QueryParams,
  ValidatedResponse,
} from "@ofqual/portal-core/core/types";
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import { InformationRequestAction } from "../domain/InformationRequestAction";
import { AssignUser } from "../domain/AssignUser";
import { InformationRequest } from "../domain/InformationRequest";
import { Message } from "../domain/Message";
import { Recipient } from "../domain/Recipient";
import {
  fromViewModel,
  RecipientPotentiallyUnknownOrg,
  RecipientsViewModel,
  toViewModel,
} from "../domain/RecipientsViewModel";
import { informationRequestService, resourceTagsService } from "../services";
import {
  ActionPayload,
  loadedItem,
  LOADING_ITEM,
  SENDING_ITEM,
  UPDATING_ITEM,
  UPDATING_ITEM_VALIDATION_FAIL,
  sentItem,
  loadedRecipients,
  LOADING_RECIPIENTS,
  UPDATING_RECIPIENTS,
  loadedActions,
  LOADING_ACTIONS,
  loadingActions,
  CREATING_ACTION,
  loadingItem,
  ASSIGN_USERS,
  SEND_MESSAGE_TO_RECIPIENTS,
  loadingRecipients,
  CREATING_DOCUMENT_ACTION,
  CREATING_MESSAGE_ACTION,
  UPDATING_ACTION,
  SEND_MESSAGE,
  UPDATE_AO_USERS,
  ASSIGN_AO_USER,
  CLOSE_RECIPIENTS,
  DELETING_ITEM,
  LOADING_ITEMS,
  loadedItems,
  READING_DOCUMENTS_ACTION,
  DELETING_ACTION,
  MARK_RESPONSE_AS_COMPLETE,
  MARK_UPLOAD_AS_COMPLETE
} from "./actions";
import { authSelector, recipientsSelector } from "./selectors";
import { ActionsState, RecipientsState } from "./state";
import { refreshActivityFeed } from "@ofqual/portal-activity-feed";

function* watchItemsSaga() {
  yield takeLatest(LOADING_ITEMS, itemsSaga);
}

function* watchItemSaga() {
  yield takeLatest(LOADING_ITEM, itemSaga);
}

function* watchDeleteItemSaga() {
  yield takeLatest(DELETING_ITEM, deleteItemSaga);
}

function* watchUpdateItemSaga() {
  yield takeEvery(UPDATING_ITEM, updateItemSaga);
}

function* watchSendingSaga() {
  yield takeEvery(SENDING_ITEM, sendItemSaga);
}

function* watchRecipientsSaga() {
  yield takeLatest(LOADING_RECIPIENTS, recipientsSaga);
}

function* watchUpdateRecipientsSaga() {
  yield takeEvery(UPDATING_RECIPIENTS, updateRecipientsSaga);
}

function* watchGetActionsSaga() {
  yield takeLatest(LOADING_ACTIONS, getActionsSaga);
}

function* watchCreateActionSaga() {
  yield takeEvery(CREATING_ACTION, createActionSaga);
}

function* watchAssignUsersSaga() {
  yield takeEvery(ASSIGN_USERS, assignUsersSaga);
}

function* watchSendMessageToRecipientsSaga() {
  yield takeEvery(SEND_MESSAGE_TO_RECIPIENTS, sendMessageToRecipientsSaga);
}

function* watchCreateDocumentActionSaga() {
  yield takeEvery(CREATING_DOCUMENT_ACTION, createDocumentActionSaga);
}

function* watchCreateMessageActionSaga() {
  yield takeEvery(CREATING_MESSAGE_ACTION, createMessageActionSaga);
}

function* watchEditActionSaga() {
  yield takeEvery(UPDATING_ACTION, editActionSaga);
}

function* watchDeleteActionSaga() {
  yield takeEvery(DELETING_ACTION, deleteActionSaga);
}

function* watchSendMessageSaga() {
  yield takeEvery(SEND_MESSAGE, sendMessageSaga);
}

function* watchUpdateAoUsersSaga() {
  yield takeEvery(UPDATE_AO_USERS, updateAoUsersSagas);
}

function* watchAssignAoUserSaga() {
  yield takeEvery(ASSIGN_AO_USER, assignAoUserSaga);
}

function* watchCloseRecipientsSaga() {
  yield takeEvery(CLOSE_RECIPIENTS, closeRecipientsSaga);
}

function* watchReadDocumentsActionSaga() {
  yield takeEvery(READING_DOCUMENTS_ACTION, readDocumentsActionSaga);
}

function* watchMarkResponseAsCompleteSaga() {
  yield takeEvery(MARK_RESPONSE_AS_COMPLETE, markResponseAsCompleteSaga);  
}

function* watchMarkUploadDocumentsAsCompleteSaga() {
  yield takeEvery(MARK_UPLOAD_AS_COMPLETE, markUploadDocumentAsCompleteSaga);  
}


function* itemsSaga({ payload }: ActionPayload<QueryParams>) {
  const { token }: ReturnType<typeof authSelector> = yield select(authSelector);

  const items: PagedResponse<InformationRequest> = yield call(
    [informationRequestService, informationRequestService.search],
    { ...payload },
    { token }
  );

  yield put(loadedItems(items));
}

function* itemSaga({ payload }: ActionPayload<string>) {
  const { token } = yield select(authSelector);

  const { informationRequest, resourceTags } = yield all({
    informationRequest: call(
      [informationRequestService, informationRequestService.get],
      payload,
      {
        token,
      }
    ),
    resourceTags: call(
      [resourceTagsService, resourceTagsService.get],
      payload,
      {
        token,
      }
    ),
  });
  informationRequest.resourceTags = resourceTags;
  yield put(loadedItem(informationRequest));
}

function* updateItemSaga({ payload }: ActionPayload<any>) {
  const { token } = yield select(authSelector);
  const { id, patchWithMeta, resourceTagsIds } = payload as {
    id: string;
    patchWithMeta: PatchWithMeta<RecipientsViewModel>;
    resourceTagsIds: number[];
  };

  const { validatedResponse } = yield all({
    validatedResponse: call(
      [informationRequestService, informationRequestService.update],
      id,
      patchWithMeta,
      {
        token,
      }
    ),
    tags: call(
      [resourceTagsService, resourceTagsService.post],
      id,
      resourceTagsIds,
      {
        token,
      }
    ),
  });

  if (validatedResponse.isValid) {
    yield put(loadingItem(validatedResponse.data.id));
  } else {
    yield put({
      type: UPDATING_ITEM_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* deleteItemSaga({ payload }: ActionPayload<string>) {
  const { token } = yield select(authSelector);
    yield all({
    informationRequest: call(
      [informationRequestService, informationRequestService.deleteCao],
      payload,
      {
        token,
      }
    )
  });
}


function* sendItemSaga({ payload }: ActionPayload<any>) {
  const { token } = yield select(authSelector);
  const { id } = payload as {
    id: string;
  };

  const validatedResponse: ValidatedResponse<InformationRequest> = yield call(
    [informationRequestService, informationRequestService.send],
    id,
    {
      token,
    }
  );

  if (validatedResponse.isValid) {
    yield put(sentItem(validatedResponse.data));
  } else {
    yield put({
      type: UPDATING_ITEM_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* recipientsSaga({
  payload,
}: ActionPayload<RecipientsState["resourceId"]>) {
  const { informationRequestId, itemId } = payload!;
  const { token } = yield select(authSelector);

  const recipients: Recipient[] = itemId
    ? yield call(
        [informationRequestService, informationRequestService.getRecipientsAo],
        informationRequestId,
        itemId!,
        { token }
      )
    : yield call(
        [informationRequestService, informationRequestService.getRecipients],
        informationRequestId,
        { token }
      );
  yield put(
    loadedRecipients(
      { informationRequestId, itemId },
      recipients,
      toViewModel(informationRequestId, recipients)
    )
  );
}

export function* updateRecipientsSaga({ payload }: ActionPayload<any>) {
  const { token } = yield select(authSelector);
  const { id, recipients, patch } = payload as {
    id: string;
    recipients: RecipientsViewModel<RecipientPotentiallyUnknownOrg>;
    patch: ChangeMeta[];
  };
  const recipientsState: RecipientsState = yield select(recipientsSelector);

  const model = fromViewModel(recipientsState.recipients!, recipients);

  const response: Recipient[] = yield call(
    [informationRequestService, informationRequestService.postRecipients],
    id,
    model,
    patch,
    {
      token,
    }
  );
  yield put(
    loadedRecipients(
      { informationRequestId: payload.id },
      response,
      toViewModel(id, response)
    )
  );
}

function* getActionsSaga({
  payload,
}: ActionPayload<ActionsState["resourceId"]>) {
  const { informationRequestId, itemId } = payload!;
  const { token } = yield select(authSelector);

  const actions: InformationRequestAction[] = itemId
    ? yield call(
        [informationRequestService, informationRequestService.getActionsAo],
        informationRequestId,
        itemId,
        { token }
      )
    : yield call(
        [informationRequestService, informationRequestService.getActions],
        informationRequestId,
        { token }
      );

  yield put(
    loadedActions({ informationRequestId, itemId }, actions)
  );
}

function* createActionSaga({
  payload,
}: ActionPayload<{
  id: string;
  files: File[];
  action: Partial<InformationRequestAction>;
}>) {
  const { token } = yield select(authSelector);
  const { id, files, action } = payload;

  yield call(
    [informationRequestService, informationRequestService.postAction],
    id,
    files,
    action,
    {
      token,
    }
  );
  yield put(loadingActions({ informationRequestId: id }));
}

function* editActionSaga({
  payload,
}: ActionPayload<{
  id: string;
  actionId: string;
  files: File[];
  attachmentIdsToDelete: string[];
  model: PatchWithMeta<InformationRequestAction>;
}>) {
  const { token } = yield select(authSelector);
  const { id, actionId, files, model, attachmentIdsToDelete } = payload;

  const updateCall = () =>
    informationRequestService.updateAction(id, actionId, files, model, {
      token,
    });

  const deleteAttachmentsCalls = () =>
    Promise.all(
      attachmentIdsToDelete.map((attachmentId) =>
        informationRequestService.deleteAttachment(id, actionId, attachmentId, {
          token,
        })
      )
    );

  yield all({
    updateCall: call(updateCall),
    deleteAttachmentsCalls: call(deleteAttachmentsCalls),
  });

  yield put(loadingActions({ informationRequestId: id }));
}

function* deleteActionSaga({
  payload,
}: ActionPayload<{
  id: string;
  actionId: string;
}>) {
  const { token } = yield select(authSelector);
  const { id, actionId } = payload;

  yield call(
    [informationRequestService, informationRequestService.deleteAction],
    id,
    actionId,
    {
      token,
    }
  );

  yield put(loadingActions({ informationRequestId: id }));
}

function* assignUsersSaga({
  payload,
}: ActionPayload<{
  resourceId: string;
  assignUsers: AssignUser[];
}>) {
  const { token } = yield select(authSelector);
  const { resourceId, assignUsers } = payload;

  const recipients: Recipient[] = yield call(
    [informationRequestService, informationRequestService.assignUsers],
    resourceId,
    assignUsers,
    {
      token,
    }
  );
  yield put(
    loadedRecipients(
      { informationRequestId: resourceId },
      recipients,
      toViewModel(resourceId, recipients)
    )
  );
}

function* sendMessageToRecipientsSaga({
  payload,
}: ActionPayload<{
  id: string;
  files: File[];
  message: Message;
}>) {
  const { token } = yield select(authSelector);
  const { id, files, message } = payload;

  yield call(
    [
      informationRequestService,
      informationRequestService.sendMessageToRecipients,
    ],
    id,
    files,
    message,
    {
      token,
    }
  );
  yield put(loadingRecipients({ informationRequestId: id }));
}

function* createDocumentActionSaga({
  payload,
}: ActionPayload<{
  id: string;
  orgRecipientId: string;
  itemId: string;
  actionId: string;
  files: File[];
}>) {
  const { token } = yield select(authSelector);
  const { id, orgRecipientId, itemId, actionId, files } = payload;

  yield call(
    [informationRequestService, informationRequestService.postDocumentAction],
    id,
    orgRecipientId,
    actionId,
    files,
    {
      token,
    }
  );
  yield put(loadingActions({ informationRequestId: id, itemId }));
}

function* createMessageActionSaga({
  payload,
}: ActionPayload<{
  id: string;
  orgRecipientId: string;
  itemId: string,
  actionId: string;
  message: string;
}>) {
  const { token } = yield select(authSelector);
  const { id, orgRecipientId, itemId, actionId, message } = payload;

  yield call(
    [informationRequestService, informationRequestService.postMessageAction],
    id,
    orgRecipientId,
    actionId,
    message,
    {
      token,
    }
  );
  yield put(loadingActions({ informationRequestId: id, itemId }));
}

function* sendMessageSaga({
  payload,
}: ActionPayload<{
  id: string;
  orgRecipientId: string;
  files: File[];
  message: string;
}>) {
  const { token } = yield select(authSelector);
  const { id, orgRecipientId, files, message } = payload;

  yield call(
    [informationRequestService, informationRequestService.sendMessage],
    id,
    orgRecipientId,
    files,
    message,
    {
      token,
    }
  );
  yield put(refreshActivityFeed(orgRecipientId));
}

function* updateAoUsersSagas({
  payload,
}: ActionPayload<{
  id: string;
  orgRecipientId: string;
  itemId: string;
  users: { id: string; upn: string }[];
}>) {
  const { token } = yield select(authSelector);
  const { id, orgRecipientId, itemId, users } = payload;

  yield call(
    [informationRequestService, informationRequestService.updateAoUsers],
    id,
    orgRecipientId,
    users.map((user) => ({ ...user, userId: user.id })),
    {
      token,
    }
  );

  yield put(
    loadingRecipients({
      informationRequestId: id,
      itemId,
    })
  );
}

function* assignAoUserSaga({
  payload,
}: ActionPayload<{
  id: string;
  orgRecipientId: string;
  itemId: string;
  aoAssignedToUpn: string;
  aoAssignedToName: string;
}>) {
  const { token } = yield select(authSelector);
  const { id, orgRecipientId, itemId, aoAssignedToUpn, aoAssignedToName } = payload;

  yield call(
    [informationRequestService, informationRequestService.assignAoUser],
    id,
    orgRecipientId,
    aoAssignedToUpn,
    aoAssignedToName,
    {
      token,
    }
  );
  yield put(loadingRecipients({ informationRequestId: id, itemId }));
  yield put(refreshActivityFeed(orgRecipientId));
}

function* closeRecipientsSaga({
  payload,
}: ActionPayload<{
  id: string;
  orgIds: string[];
}>) {
  const { token } = yield select(authSelector);
  const { id, orgIds } = payload;

  yield call(
    [informationRequestService, informationRequestService.closeRecipients],
    id,
    orgIds,
    {
      token,
    }
  );

  yield put(
    loadingRecipients({
      informationRequestId: id
    })
  );
}

function* readDocumentsActionSaga({
  payload,
}: ActionPayload<{
  id: string;
  orgRecipientId: string;
  itemId: string,
  actionId: string;
}>) {
  const { token } = yield select(authSelector);
  const { id, orgRecipientId, itemId, actionId } = payload;

  yield call(
    [informationRequestService, informationRequestService.readDocuments],
    id,
    orgRecipientId,
    actionId,
    {
      token,
    }
  );
  yield put(loadingActions({ informationRequestId: id, itemId }));
}

function* markResponseAsCompleteSaga({
  payload,
}: ActionPayload<{
  id: string;
  orgRecipientId: string;
  itemId: string,
  actionId: string;
}>) {
  const { token } = yield select(authSelector);
  const { id, orgRecipientId, itemId, actionId } = payload;

  yield call(
    [informationRequestService, informationRequestService.markResponseComplete],
    id,
    orgRecipientId,
    actionId,
    {
      token,
    }
  );
  yield put(loadingActions({ informationRequestId: id, itemId }));
}

function* markUploadDocumentAsCompleteSaga({
  payload,
}: ActionPayload<{
  id: string;
  orgRecipientId: string;
  itemId: string,
  actionId: string;
}>) {
  const { token } = yield select(authSelector);
  const { id, orgRecipientId, itemId, actionId } = payload;

  yield call(
    [informationRequestService, informationRequestService.markUploadComplete],
    id,
    orgRecipientId,
    actionId,
    {
      token,
    }
  );
  yield put(loadingActions({ informationRequestId: id, itemId }));
}

export function* rootSaga() {
  yield all([
    watchItemsSaga(),
    watchItemSaga(),
    watchUpdateItemSaga(),
    watchDeleteItemSaga(),
    watchSendingSaga(),
    watchRecipientsSaga(),
    watchUpdateRecipientsSaga(),
    watchGetActionsSaga(),
    watchCreateActionSaga(),
    watchAssignUsersSaga(),
    watchSendMessageToRecipientsSaga(),
    watchCreateDocumentActionSaga(),
    watchCreateMessageActionSaga(),
    watchEditActionSaga(),
    watchSendMessageSaga(),
    watchUpdateAoUsersSaga(),
    watchAssignAoUserSaga(),
    watchCloseRecipientsSaga(),
    watchReadDocumentsActionSaga(),
    watchDeleteActionSaga(),
    watchMarkResponseAsCompleteSaga(),
    watchMarkUploadDocumentsAsCompleteSaga(),
  ]);
}
